/**
 * Decision Renderer - User choice branching points
 */

import React, { useState, useEffect } from 'react';
import { DecisionConfig, FlowContext } from '../core/types';
import { GuidedFlowController } from '../core/GuidedFlowController';
import { motion, AnimatePresence } from 'motion/react';

export interface DecisionRendererProps {
  config: DecisionConfig;
  context: FlowContext;
  controller: GuidedFlowController;
  onDecision: (nextStepId: string) => void;
  autoProgressEnabled?: boolean;
}

export const DecisionRenderer: React.FC<DecisionRendererProps> = ({
  config,
  context,
  controller,
  onDecision,
  autoProgressEnabled = false,
}) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleDecision = (optionId: string) => {
    if (isProcessing) return;

    setSelectedOption(optionId);
    setIsProcessing(true);

    const option = config.options.find((opt) => opt.id === optionId);
    if (!option) return;

    // Track decision
    const providers = controller.getProviders();
    providers.analytics.track('flow:decision', {
      flowId: context.flowId,
      stepId: context.currentStepId,
      optionId: option.id,
      optionLabel: option.label,
    });

    // Navigate after brief delay for visual feedback
    setTimeout(() => {
      if (option.nextStep) {
        onDecision(option.nextStep);
      }
    }, 800);
  };

  // Auto-select in demo mode (only if toggle is enabled)
  useEffect(() => {
    if (config.autoSelectInDemo && context.mode === 'demo' && autoProgressEnabled) {
      const delay = config.autoSelectDelay || 2000;
      const timer = setTimeout(() => {
        handleDecision(config.autoSelectInDemo!);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [config.autoSelectInDemo, config.autoSelectDelay, context.mode, autoProgressEnabled]);

  // Render based on style
  const renderCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl w-full">
      {config.options.map((option, index) => (
        <motion.button
          key={option.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          onClick={() => handleDecision(option.id)}
          disabled={isProcessing}
          className={`
            relative p-6 rounded-xl border-2 text-left transition-all
            ${selectedOption === option.id
              ? 'border-primary bg-primary/20 scale-105'
              : 'border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20'
            }
            ${option.recommended ? 'ring-2 ring-primary/50' : ''}
            ${isProcessing && selectedOption !== option.id ? 'opacity-50' : ''}
            disabled:cursor-not-allowed
          `}
        >
          {/* Recommended badge */}
          {option.recommended && !selectedOption && (
            <div className="absolute -top-3 left-6 px-3 py-1 bg-primary rounded-full text-white text-xs font-medium">
              Recommended
            </div>
          )}

          {/* Icon */}
          {option.icon && (
            <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center mb-4 text-2xl">
              {option.icon === 'add' && '➕'}
              {option.icon === 'forward' && '▶️'}
              {option.icon === 'upload' && '📤'}
              {option.icon === 'template' && '📋'}
              {option.icon === 'meeting' && '🎥'}
            </div>
          )}

          {/* Content */}
          <div className="text-white font-semibold text-lg mb-2">
            {option.label}
          </div>
          {option.description && (
            <div className="text-white/70 text-sm">
              {option.description}
            </div>
          )}

          {/* Selected indicator */}
          {selectedOption === option.id && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute top-4 right-4 w-6 h-6 rounded-full bg-primary flex items-center justify-center text-white text-sm"
            >
              ✓
            </motion.div>
          )}
        </motion.button>
      ))}
    </div>
  );

  const renderButtons = () => (
    <div className="flex flex-col gap-3 max-w-md w-full">
      {config.options.map((option, index) => (
        <motion.button
          key={option.id}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          onClick={() => handleDecision(option.id)}
          disabled={isProcessing}
          className={`
            px-6 py-4 rounded-full text-left transition-all
            ${selectedOption === option.id
              ? 'bg-primary text-white scale-105'
              : 'bg-white/10 text-white hover:bg-white/20'
            }
            ${isProcessing && selectedOption !== option.id ? 'opacity-50' : ''}
            disabled:cursor-not-allowed
          `}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">{option.label}</div>
              {option.description && (
                <div className="text-sm text-white/70 mt-1">
                  {option.description}
                </div>
              )}
            </div>
            {selectedOption === option.id && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="text-xl"
              >
                ✓
              </motion.div>
            )}
          </div>
        </motion.button>
      ))}
    </div>
  );

  return (
    <div className="flex items-center justify-center h-full p-6">
      <div className="text-center w-full">
        {/* Question */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="text-3xl font-bold text-white mb-2">
            {config.question}
          </div>
        </motion.div>

        {/* Options */}
        <div className="flex justify-center">
          {config.style === 'cards' ? renderCards() : renderButtons()}
        </div>

        {/* Processing indicator */}
        <AnimatePresence>
          {isProcessing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mt-6 text-white/70"
            >
              <div className="flex items-center justify-center gap-2">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                  className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full"
                />
                <span>Processing your choice...</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
